import { TaskCategory } from '@/types/task';
import type { Task } from '@/types/task';
import type { WeatherData } from '@/services/weather-service';
import { localTodayISO, toLocalISODate } from '@/utils/date-utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface WeatherConflict {
  taskId: string;
  taskTitle: string;
  taskCategory: string;
  dueDate: string;              // ISO date yyyy-MM-dd
  weatherConcern: string;       // Human-readable: "Rain (80% chance)"
  suggestedDate: string | null; // ISO date of next suitable day, null if none within ±windowDays
  suggestedReason: string;      // "Wednesday is clear with moderate temps"
  severity: 'warning' | 'caution';
}

export interface WeatherTaskAnalysisResult {
  conflicts: WeatherConflict[];
  analyzedTasks: number;
  forecastDays: number;
  generatedAt: Date;
}

export interface DayWeatherEvaluation {
  hasRain: boolean;
  hasHighWind: boolean;
  hasExtremeCold: boolean;
  summary: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const WEATHER_SENSITIVE_CATEGORIES: TaskCategory[] = [
  TaskCategory.FoodSource,
  TaskCategory.HabitatManagement,
  TaskCategory.StandPrep,
];

const RAIN_POP_THRESHOLD = 0.6;     // >60% precipitation probability
const WIND_MPH_THRESHOLD = 20;      // >20 mph daily wind speed
const COLD_TEMP_THRESHOLD = 15;     // <15°F daily low
const ALTERNATIVE_WINDOW_DAYS = 3;  // ±3 days search window

const RAIN_CONDITION_KEYWORDS = ['rain', 'storm', 'thunderstorm', 'drizzle', 'shower'];

// ---------------------------------------------------------------------------
// Day weather evaluation
// ---------------------------------------------------------------------------

/**
 * Evaluate a single day from WeatherData.daily[] for weather conflicts.
 * Uses imperial units (°F, mph) matching OWM One Call 3.0 with units=imperial.
 */
export function evaluateDayWeather(
  dailyEntry: WeatherData['daily'][number],
): DayWeatherEvaluation {
  const conditionLower = (dailyEntry.conditionDetail ?? dailyEntry.condition ?? '').toLowerCase();

  const hasRain =
    dailyEntry.pop > RAIN_POP_THRESHOLD ||
    RAIN_CONDITION_KEYWORDS.some((kw) => conditionLower.includes(kw));

  const hasHighWind = dailyEntry.windSpeed > WIND_MPH_THRESHOLD;
  const hasExtremeCold = dailyEntry.tempMin < COLD_TEMP_THRESHOLD;

  const concerns: string[] = [];
  if (hasRain) {
    const pct = Math.round(dailyEntry.pop * 100);
    concerns.push(pct > 0 ? `Rain (${pct}% chance)` : 'Rain forecast');
  }
  if (hasHighWind) concerns.push(`High wind (${Math.round(dailyEntry.windSpeed)} mph)`);
  if (hasExtremeCold) concerns.push(`Extreme cold (${Math.round(dailyEntry.tempMin)}°F)`);

  return {
    hasRain,
    hasHighWind,
    hasExtremeCold,
    summary: concerns.join(', ') || 'Clear',
  };
}

// ---------------------------------------------------------------------------
// Alternative date finder
// ---------------------------------------------------------------------------

/**
 * Find the nearest clear day within ±windowDays of dueDate.
 * Prefers days after the due date, then before.
 */
export function findAlternativeDate(
  dueDate: string,
  daily: WeatherData['daily'],
  windowDays = ALTERNATIVE_WINDOW_DAYS,
): { date: string; reason: string } | null {
  if (daily.length === 0) return null;

  // Build a map from ISO date string → daily entry
  const byDate = new Map<string, WeatherData['daily'][number]>();
  for (const entry of daily) {
    const iso = toLocalISODate(new Date(entry.dt * 1000));
    byDate.set(iso, entry);
  }

  // Candidate offsets: prefer +1, +2, +3, then -1, -2, -3
  const offsets: number[] = [];
  for (let i = 1; i <= windowDays; i++) offsets.push(i);
  for (let i = 1; i <= windowDays; i++) offsets.push(-i);

  for (const offset of offsets) {
    // Parse dueDate (YYYY-MM-DD) into LOCAL date components rather than
    // `new Date(dueDate)` (which parses a date-only string as UTC midnight).
    // Mixing a UTC-anchored Date with local getDate()/setDate() calls shifts
    // the candidate by a day in any non-UTC timezone — build the candidate
    // as a local-midnight Date from the start so it stays consistent with
    // the local-anchored `byDate` map above.
    const [dueYear, dueMonth, dueDay] = dueDate.split('-').map(Number);
    const candidate = new Date(dueYear, dueMonth - 1, dueDay);
    candidate.setDate(candidate.getDate() + offset);
    const candidateStr = toLocalISODate(candidate);
    const entry = byDate.get(candidateStr);
    if (!entry) continue;

    const eval_ = evaluateDayWeather(entry);
    if (!eval_.hasRain && !eval_.hasHighWind && !eval_.hasExtremeCold) {
      const dayName = candidate.toLocaleDateString('en-US', { weekday: 'long' });
      const dateLabel = candidate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return {
        date: candidateStr,
        reason: `${dayName} (${dateLabel}) is clear with moderate conditions`,
      };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Core analysis
// ---------------------------------------------------------------------------

/**
 * Cross-reference upcoming tasks against the 8-day daily forecast.
 * Returns conflicts for weather-sensitive tasks whose due dates have bad weather.
 */
export function analyzeWeatherConflicts(
  tasks: Task[],
  daily: WeatherData['daily'],
  sensitiveCategories: TaskCategory[] = WEATHER_SENSITIVE_CATEGORIES,
): WeatherTaskAnalysisResult {
  if (daily.length === 0 || tasks.length === 0) {
    return { conflicts: [], analyzedTasks: 0, forecastDays: 0, generatedAt: new Date() };
  }

  // Build set of forecast dates for quick lookup
  const forecastDates = new Map<string, WeatherData['daily'][number]>();
  for (const entry of daily) {
    const iso = toLocalISODate(new Date(entry.dt * 1000));
    forecastDates.set(iso, entry);
  }

  // Filter: incomplete, not snoozed, in sensitive categories, due within forecast
  const today = localTodayISO();
  const qualifying = tasks.filter((t) => {
    if (t.completed) return false;
    if (t.snoozedUntil && t.snoozedUntil >= today) return false;
    if (!t.category || !sensitiveCategories.includes(t.category as TaskCategory)) return false;
    if (!forecastDates.has(t.dueDate)) return false;
    return true;
  });

  const conflicts: WeatherConflict[] = [];

  for (const task of qualifying) {
    const forecastEntry = forecastDates.get(task.dueDate);
    if (!forecastEntry) continue;

    const eval_ = evaluateDayWeather(forecastEntry);
    const hasConflict = eval_.hasRain || eval_.hasHighWind || eval_.hasExtremeCold;
    if (!hasConflict) continue;

    // severity: warning for rain/cold (definite impact), caution for wind-only
    const severity: 'warning' | 'caution' =
      eval_.hasRain || eval_.hasExtremeCold ? 'warning' : 'caution';

    const alternative = findAlternativeDate(task.dueDate, daily);

    conflicts.push({
      taskId: task.id,
      taskTitle: task.title,
      taskCategory: task.category ?? '',
      dueDate: task.dueDate,
      weatherConcern: eval_.summary,
      suggestedDate: alternative?.date ?? null,
      suggestedReason: alternative?.reason ?? '',
      severity,
    });
  }

  // Sort by due date ascending
  conflicts.sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  return {
    conflicts,
    analyzedTasks: qualifying.length,
    forecastDays: daily.length,
    generatedAt: new Date(),
  };
}
